import { LUIGI_DELETED_STATUS, isAppModule } from '@src/utils/constants';
import { useCallback, useEffect } from 'react';
import { useSetAtom, useAtomValue } from 'jotai';
import {
  errorResponseAtom,
  errorDataAtom,
  activeTabAtom,
  Tabs,
} from '@src/components/metrics-panel-hz/atoms/index';
import { useSelector } from 'react-redux';
import {
  realTimeModeOnTrendsSelector,
  supportedMetricsSelector,
  getFirstAppliedFilter,
} from '@src/store/common/selectors';
import { allDimensionsListSelector } from '@src/store/common/dimensionList/selectors';
import { activeDashboardSelector } from '@src/store/tempDashboard/selectors';
import { cloneDeep, isEqual } from 'lodash';
import { metricSelector } from '@store/metricsChartContainer/selectors';
import { selectedMetricsAtom } from '@src/components/app-viewer-sessions/atoms';
import { dimensionMetricsSelector, dimensionsSelector } from '@store/trends/base/selectors';
import { useWidgetStateForSave } from '@src/components/widget-panel/state/dashboard';
import { ECO_RESERVED_DIMENSIONS } from '@src/components/app-viewer-sessions/utils';

export const useResetError = () => {
  const setErrorResponse = useSetAtom(errorResponseAtom);
  const setErrorData = useSetAtom(errorDataAtom);
  const errorData = useAtomValue(errorDataAtom);
  return useCallback(() => {
    if (errorData.shouldBeReset) {
      setErrorResponse({});
      resetErrorData(setErrorData);
    }
  }, [setErrorResponse, setErrorData, errorData]);
};

const useGetCurrentSelectedKeys = () => {
  const activeTab = useAtomValue(activeTabAtom);
  // metrics card
  const metrics = useSelector(metricSelector) || [];
  // metrics in dimension table
  const metricsInDimensionTable: string[] = useSelector(dimensionMetricsSelector) || [];
  // dimension card
  const dimensions: string[] = useSelector(dimensionsSelector) || [];
  // users table
  const selectedMetrics: any = useAtomValue(selectedMetricsAtom);
  // metrics in widget
  const widgetState = useWidgetStateForSave();

  const widgets = widgetState?.widgets;
  const selectedWidgetsMetricsKeys = [] as string[];

  Object.keys(widgets || {}).forEach((key) => {
    const widget = widgets[key];
    const metrics = widget?.state?.metrics || [];
    metrics.forEach((metric) => {
      selectedWidgetsMetricsKeys.push(metric);
    });
  });

  const selectedKeys =
    activeTab === Tabs.MetricCharts
      ? [...new Set([...metrics, ...metricsInDimensionTable, ...dimensions, ...selectedMetrics])]
      : [
          ...new Set([
            ...metricsInDimensionTable,
            ...dimensions,
            ...selectedWidgetsMetricsKeys,
            ...selectedMetrics,
          ]),
        ];
  return selectedKeys;
};

export const useSetError = () => {
  const setErrorResponse = useSetAtom(errorResponseAtom);
  const setErrorData = useSetAtom(errorDataAtom);
  const errorData = useAtomValue(errorDataAtom);
  const selectedKeys = useGetCurrentSelectedKeys();
  const realTimeOnTrends = useSelector(realTimeModeOnTrendsSelector);
  const supportedMetrics = useSelector(supportedMetricsSelector) as Record<string, any>;
  const dimensionsList = useSelector(allDimensionsListSelector) as any[];
  const { usecaseKey } = useSelector(activeDashboardSelector);

  const notDimension = useCallback(
    (key) => {
      return (
        !dimensionsList.some((dimension) => dimension.field === key) &&
        !ECO_RESERVED_DIMENSIONS.includes(key)
      );
    },
    [dimensionsList],
  );

  useEffect(() => {
    // remove fields that are not selected anymore
    if (selectedKeys.length > 0) {
      const selectedKeysSet = new Set(selectedKeys);
      const errorDataCopy = cloneDeep(errorData);
      const { fields: errorDataKeys } = errorDataCopy;

      // update fields, remove fields that are not selected anymore
      const updatedFields = errorDataKeys.reduce((acc: string[], key: string) => {
        const baseKey = key.split('.')[1];
        if (selectedKeysSet.has(baseKey)) {
          acc.push(key);
        } else {
          delete errorDataCopy[baseKey];
        }
        return acc;
      }, []);

      // check if selected metrics are supported
      selectedKeys.forEach((key: string) => {
        // add a prefix to keep consistency with errorData
        const prefixedKey = `session.${key}`;
        if (!supportedMetrics[key] && notDimension(key) && !updatedFields.includes(prefixedKey)) {
          updatedFields.push(prefixedKey);
          errorDataCopy[key] = true;
        }
      });

      errorDataCopy.fields = updatedFields;
      if (isEqual(errorData, errorDataCopy)) return;
      setErrorData(errorDataCopy);
    }
  }, [selectedKeys, errorData, supportedMetrics, setErrorData, notDimension]);

  const handleError = useCallback(
    (event) => {
      const response = event.detail.data;
      const isPresetError = isAppModule && !!response?.presetKey;
      const { error } = makeErrorData(
        errorData,
        setErrorData,
        response,
        !realTimeOnTrends && !isPresetError,
        {
          shouldBeReset: isPresetError,
        },
      );
      setErrorResponse(error);
    },
    [realTimeOnTrends, errorData, setErrorData, setErrorResponse],
  );

  useEffect(() => {
    document.addEventListener('LUIGI_DELTED_STATUS', handleError);
    return () => {
      document.removeEventListener('LUIGI_DELTED_STATUS', handleError);
    };
  }, [handleError, usecaseKey]);
};

const makeErrorData = (
  errorData,
  setErrorData,
  data,
  mergePrevError,
  options = { shouldBeReset: false },
) => {
  const { shouldBeReset } = options;
  const error: any = mergePrevError ? cloneDeep(errorData) : { fields: [], filters: [] };
  error.shouldBeReset = shouldBeReset;
  if (data.code === LUIGI_DELETED_STATUS) {
    data.error.fields.forEach((f) => {
      const field = f.split('.')[1];
      error[field] = true;
    });
    data.error.filters?.forEach((f) => {
      const field = f.split('.')[1];
      error[field] = true;
    });
    error['fields'] = [...error.fields, ...data.error.fields];
    error['filters'] = [...error.filters, ...data.error.filters];
  }
  if (!isEqual(errorData, error)) {
    setErrorData(error);
  }

  return { error };
};

const resetErrorData = (setErrorData) => {
  setErrorData({
    shouldBeReset: false,
    fields: [],
    filters: [],
  });
};

export const useFilterContainsInvalidField = (appliedFilter?: any) => {
  let filterError = false;
  const globalFilter = useSelector(getFirstAppliedFilter);
  const curFilter = appliedFilter || globalFilter;
  const errorData = useAtomValue(errorDataAtom);
  curFilter.rule?.forEach((rule) => {
    rule.forEach((r) => {
      if (errorData[r.field] === true) filterError = true;
    });
  });
  return filterError;
};
